import { useMemo } from 'react';

import { State } from 'modules';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import { DEFAULT_PAGE_SIZE } from 'constants/common';
import {
  changePasswordEndpoint,
  inviteUserEndpoint,
  userListEndpoint,
  userRightsEndpoint,
  userSettingsEndpoint,
  userSingleEndpoint,
} from 'constants/endpoints';

import { appSelectors } from 'selectors';

import { getPageNumberAfterLoadMore } from 'utils/helper';
import { httpMethod, simplifyBuilder } from 'utils/sra';

import {
  APISortOrder,
  APIUser,
  APIUserInvite,
  APIUserRightRequest,
  APIUserSearchFilter,
  APIUserSearchResult,
  APIUserSettingModel,
} from 'types/api';

export type UserState = {
  usersList: APIUser[];
  user: APIUser | null;
  loadUserPending: boolean;
  loadUsersPending: boolean;
  allUsersLoaded: boolean;
  inviteUserPending: boolean;
  usersListLoaded: boolean;
  filters: APIUserSearchFilter;
  pagination: {
    pageNumber: APIUserSearchResult['pageNumber'];
    pageSize: APIUserSearchResult['pageSize'];
  };
  userSettings: APIUserSettingModel[];
  loadUserSettingsPending: boolean;
  updateUserSettingPending: boolean;
  changeUserPasswordPending: boolean;
};

export const userState: UserState = {
  usersList: [],
  user: null,
  loadUserPending: false,
  filters: {
    searchSortOrders: [
      {
        sortField: 'Id',
        sortOrder: APISortOrder.DESC,
      },
    ],
  },
  inviteUserPending: false,
  loadUsersPending: false,
  usersListLoaded: false,
  allUsersLoaded: false,
  pagination: { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
  userSettings: [],
  loadUserSettingsPending: false,
  updateUserSettingPending: false,
  changeUserPasswordPending: false,
};

const builder = simplifyBuilder(userState, {});

const updateUserFilters = builder.createReduxAction(
  (filters: APIUserSearchFilter) => ({
    name: 'updateUserFilters',
    updater: () => ({
      filters,
    }),
  }),
);

export const loadUsers = builder.createServerAction(
  (tenantId: string) => ({
    name: 'loadUsers',
    url: userListEndpoint(tenantId),
    body: (state: State) => ({
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      ...state.user.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: UserState, payload: APIUserSearchResult) => ({
      usersList: payload.result,
      usersListLoaded: true,
      allUsersLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadMoreUsers = builder.createServerAction(
  (tenantId: string) => ({
    name: 'loadMoreUsers',
    url: userListEndpoint(tenantId),
    body: (state: State) => ({
      pageNumber: getPageNumberAfterLoadMore(
        state.user.usersList,
        state.user.pagination.pageSize,
      ),
      ...state.user.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: UserState, payload: APIUserSearchResult) => ({
      usersList: [...(state.usersList || []), ...payload.result],
      usersListLoaded: true,
      allUsersLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadUser = builder.createServerAction(
  (tenantId: string, userId: string) => ({
    name: 'loadUser',
    url: userSingleEndpoint(tenantId, userId),
    method: httpMethod.get,
    onSuccess: (state: UserState, payload: APIUser) => ({
      user: payload,
    }),
  }),
);

export const deleteUser = builder.createServerAction(
  (tenantId: string, userId: string) => ({
    name: 'deleteUser',
    url: userSingleEndpoint(tenantId, userId),
    method: httpMethod.delete,
    onSuccess: (state: UserState) => ({
      usersList: state.usersList.filter(user => user.id !== userId),
    }),
  }),
);

export const updateUser = builder.createServerAction(
  (tenantId: string, userId: string, model: APIUser) => ({
    name: 'updateUser',
    url: userSingleEndpoint(tenantId, userId),
    method: httpMethod.post,
    body: model,
    onSuccess: (state: UserState, result: APIUser) => ({
      usersList: state.usersList.map(user =>
        user.id === userId ? result : user,
      ),
    }),
  }),
);

export const createUser = builder.createServerAction(
  (tenantId: string, model: APIUser) => ({
    name: 'createUser',
    url: userListEndpoint(tenantId),
    method: httpMethod.put,
    body: { ...model },
    onSuccess: (state: UserState, result: APIUser) => ({
      usersList: [result, ...state.usersList],
    }),
  }),
);

export const inviteUser = builder.createServerAction(
  (tenantId: string, model: APIUserInvite) => ({
    name: 'inviteUser',
    url: inviteUserEndpoint(tenantId),
    method: httpMethod.put,
    body: { ...model },
    onSuccess: (state: UserState, result: APIUser) => ({
      usersList: [result, ...state.usersList],
    }),
  }),
);

export const updateUserRights = builder.createServerAction(
  (tenantId: string, userId: string, rights: APIUserRightRequest) => ({
    name: 'updateUserRights',
    url: userRightsEndpoint(tenantId, userId),
    method: httpMethod.post,
    body: rights,
    onSuccess: (state: UserState, result: APIUser) => ({
      usersList: state.usersList.map(user =>
        user.id === userId ? result : user,
      ),
      user: result,
    }),
  }),
);

export const loadUserSettings = builder.createServerAction(() => ({
  name: 'loadUserSettings',
  url: userSettingsEndpoint(),
  method: httpMethod.get,
  onSuccess: (state: UserState, payload: APIUserSettingModel[]) => ({
    userSettings: payload,
  }),
}));

export const updateUserSettings = builder.createServerAction(
  (setting: APIUserSettingModel) => ({
    name: 'updateUserSettings',
    url: userSettingsEndpoint(),
    method: httpMethod.put,
    body: setting,
    onSuccess: (state: UserState, result: APIUserSettingModel) => ({
      userSettings: state.userSettings.map(s =>
        s.type === result.type ? result : s,
      ),
    }),
  }),
);

export const clearUser = builder.createReduxAction(() => ({
  name: 'clearUser',
  updater: () => ({
    user: null,
  }),
}));

export const clearUserList = builder.createReduxAction(() => ({
  name: 'clearUserList',
  updater: () => ({
    usersList: [],
  }),
}));

const clearUserFilters = builder.createReduxAction(() => ({
  name: 'clearUserFilters',
  updater: () => ({
    filters: userState.filters,
    pagination: userState.pagination,
  }),
}));

const changeUserPassword = builder.createServerAction(
  (currentPassword: string, newPassword: string) => ({
    name: 'changeUserPassword',
    url: changePasswordEndpoint(),
    method: httpMethod.post,
    body: { currentPassword, newPassword },
    onPending: (state: UserState) => ({
      ...state,
      changeUserPasswordPending: true,
    }),
    onSuccess: (state: UserState) => ({
      ...state,
      changeUserPasswordPending: false,
    }),
    onError: (state: UserState) => ({
      ...state,
      changeUserPasswordPending: false,
    }),
  }),
);

export const useUser = () => {
  const dispatch = useDispatch();
  const tenantId = useSelector(appSelectors.tenantId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          updateUserFilters,
          clearUser,
          clearUserFilters,
          clearUserList,
          changeUserPassword: changeUserPassword,
          inviteUser: inviteUser.bind(null, tenantId),
          updateUserRights: updateUserRights.bind(null, tenantId),
          createUser: createUser.bind(null, tenantId),
          updateUser: updateUser.bind(null, tenantId),
          deleteUser: deleteUser.bind(null, tenantId),
          loadUser: loadUser.bind(null, tenantId),
          loadUsers: loadUsers.bind(null, tenantId),
          loadMoreUsers: loadMoreUsers.bind(null, tenantId),
          loadUserSettings,
          updateUserSettings,
        },
        dispatch,
      ),
    [dispatch, tenantId],
  );
};

export const userReducer = builder.getReducers();
